<template>
  <!-- Main Account Edit Dialog -->
  <div v-if="show" class="overlay">
    <div class="account-dialog">
      <h2 class="dialog-title">Edit Account Info</h2>
      <form @submit.prevent="saveAccountInfo">
        
        <!-- Account ID -->
        <div class="account-id-display">
          <strong>Account ID: </strong>
          <span>{{ localAccount.accountId }}</span>
        </div>

        <!-- Email frequency -->
        <div class="frequency-section dialog-form-group">
          <label for="frequency-select">Email Frequency:</label>
          <div class="frequency-controls">
            <select id="frequency-select" v-model="selectedFrequency" class="frequency-select">
              <option value="1-2">1-2 times/week</option>
              <option value="5-7">5-7 times/week</option>
              <option value="10-12">10-12 times/week</option>
            </select>
          </div>
        </div>

        <!-- Message delivery method -->
        <div class="delivery-method-section dialog-form-group">
          <label>How your messages are delivered to you:</label>
          <div class="delivery-method-buttons">
            <button type="button"
              :class="['delivery-btn', { 'delivery-btn-active': selectedDeliveryMethod === 'Email' }]"
              @click="(selectedDeliveryMethod === 'SMS' || selectedDeliveryMethod === 'SmsAwaitingConfirmation') ? (isIncludedSms ? disableIncludedSms() : showDisableSmsConfirmation = true) : null">
              📧 Email
            </button>
            <button type="button"
              :class="['delivery-btn', { 'delivery-btn-active': selectedDeliveryMethod === 'SMS' || selectedDeliveryMethod === 'SmsAwaitingConfirmation' }]"
              @click="selectedDeliveryMethod !== 'SMS' && selectedDeliveryMethod !== 'SmsAwaitingConfirmation' ? showSmsOptIn = true : null">
              💬 Text (SMS)
            </button>
          </div>
        </div>

        <!-- Confirmation notice for users awaiting SMS confirmation -->
        <div v-if="selectedDeliveryMethod === 'SmsAwaitingConfirmation'" class="sms-awaiting-notice">
          📱 Check your texts and reply <strong>YES</strong> to the message we sent you to complete SMS setup.
        </div>

        <!-- Phone number (SMS users only) -->
        <div v-if="selectedDeliveryMethod === 'SMS' || selectedDeliveryMethod === 'SmsAwaitingConfirmation'" class="dialog-form-group">
          <label for="account-phone">Mobile Phone Number:</label>
          <div class="dialog-phone-row">
            <span class="dialog-phone-prefix">+1</span>
            <input
              id="account-phone"
              :value="localAccount.phoneNumber"
              type="tel"
              maxlength="10"
              autocomplete="tel"
              @input="onPhoneInput"
            />
          </div>
          <p v-if="phoneNumberError" class="verification-warning">{{ phoneNumberError }}</p>
        </div>

        <!-- Name-->
        <div class="dialog-form-group">
          <label for="account-name">Name:</label>
          <input id="account-name" v-model="localAccount.name" type="text" required />
        </div>

        <!-- Email -->
        <div class="dialog-form-group">
          <label for="account-email">Email:</label>
          <div class="dialog-phone-row">
          <input id="account-email" v-model="localAccount.email" type="email" required />
          <!-- If email has been changed, show "Verify" button. If the "Verify" button is clicked, show spinner. Otherwise, show checkmark -->
          <button v-if="!isEmailVerified" class="dialog-validate-btn" type="button" @click="requestEmailVerification" :disabled="isRequestingCode || localAccount.email === originalEmail">
            <span v-if="!isRequestingCode">Verify</span>
            <span v-else class="spinner"></span>
          </button>
          <span v-else class="verification-checkmark">✓</span>
          </div>
          <!-- If email has been changed and not verified, show instruction to verify email address before saving -->
          <p v-if="emailChanged && !isEmailVerified" class="verification-warning">
            Please verify your new email address before saving.
          </p>
          <div v-if="emailChangeCodeRequestError" class="error-message">{{ emailChangeCodeRequestError }}</div>
        </div>

        <!-- Main Account Edit Dialog Action Buttons: Cancel, Save (spinner if clicked) -->
        <div class="dialog-actions">
          <!-- Cancel button -->
          <button class="dialog-cancel-btn" type="button" @click="close" :disabled="isAccountSaving">Cancel</button>
          <!-- Save button & spinner -->
          <button class="dialog-save-btn" type="submit" :disabled="isAccountSaving">
            <span v-if="!isAccountSaving">Save</span>
            <span v-else class="spinner"></span>
          </button>
        </div>
      </form>

      <!-- Delete Account link (separate from buttons @ bottom of main dialog)-->
      <div class="delete-account-section">
        <a
          v-if="selectedDeliveryMethod === 'SMS' || selectedDeliveryMethod === 'SmsAwaitingConfirmation'"
          href="#"
          class="manage-subscription-link"
          @click.prevent="manageSubscription"
        >
          <span v-if="!isManagingSubscription">Manage Subscription</span>
          <span v-else class="spinner" style="width:14px;height:14px;border-color:rgba(125,164,126,0.3);border-top-color:#7da47e;"></span>
        </a>
        <a href="#" class="delete-account-link" @click.prevent="onDeleteAccountClick">
          Permanently Delete Account
        </a>
      </div>
    </div> 
  </div><!-- End of main account edit dialog & conditional dialogs -->

  <!-- CONDITIONALLY DISPLAYED DIALOGS -->

  <!-- Account Updated Message (Saved changes successful) -->
  <div v-if="showAccountUpdatedMessage" class="overlay">
    <div class="message">
      <div class="account-updated-icon">✓</div>
      <h3 class="dialog-title">Account Updated</h3>
      <p class="account-updated-text">Account information updated successfully!</p>
    </div>
  </div>

  <!-- VALIDATING A NEW EMAIL ADDRESS -->
  <!-- Email Verification Required Message -->
  <div v-if="showEmailVerificationRequiredMessage" class="overlay">
    <div class="message">
      <h3 class="dialog-title">Email Verification Required</h3>
      <p class="email-verification-required-text">Please verify your new email address before saving.</p>
    </div>
  </div>

  <!-- Enter code dialog to verify newly edited email (user has clicked Verify on new email address) -->
  <div v-if="show && showVerificationDialog" class="overlay" style="z-index:2100;">
    <div class="account-dialog">
      <h2 class="dialog-title">Enter Verification Code</h2>
      <form @submit.prevent="submitVerificationCode">
        <div class="dialog-form-group">
          <label for="verification-code">Verification Code:</label>
          <!-- Enter verification code here -->
          <input id="verification-code" v-model="verificationCode" type="text" maxlength="8" autocomplete="one-time-code" required />
        </div>
        <!-- A spot to display a message if there's an error verifying the code -->
        <div v-if="verificationError" class="verification-warning">{{ verificationError }}</div>
        <!-- Verification code dialog action buttons Cancel & Verify (with spinner)-->
        <div class="dialog-actions">
          <button class="dialog-cancel-btn" type="button" @click="closeVerificationDialog">Cancel</button>
          <button class="dialog-save-btn" type="submit" :disabled="isValidatingCode">
            <span v-if="!isValidatingCode">Verify</span>
            <span v-else class="spinner"></span>
          </button>
        </div>
      </form>
    </div>
  </div>

  <!-- Account Update Attempts Exceeded Dialog -->
  <div v-if="showAccountUpdateAttemptsExceededDialog" class="overlay">
    <div class="account-dialog attempts-exceeded-dialog">
      <h2 class="dialog-title">That's Too Many Tries</h2>
      <div class="attempts-exceeded-content">
        <p class="attempts-exceeded-message">
          We only allow 3 tries.
        </p>
        <p class="attempts-exceeded-suggestion">
          Please request a new verification code to try again.
        </p>
      </div>
      <div class="dialog-actions attempts-exceeded-actions">
        <button class="dialog-cancel-btn" type="button" @click="showAccountUpdateAttemptsExceededDialog = false">Cancel</button>
        <button class="dialog-save-btn" type="button" @click="restartEmailVerificationProcess">Request New Code</button>
      </div>
    </div>
  </div>

  <!-- Email Verified Message -->
  <div v-if="showEmailVerifiedMessage" class="overlay">
    <div class="message">
      <div class="email-verified-icon">✓</div>
      <h3 class="dialog-title">Email Verified</h3>
      <p class="email-verified-text">Email verified successfully! You can now save your changes.</p>
    </div>
  </div>

  <!-- DELETING ACCOUNT -->
  <!-- Delete Account Dialog (user has clicked Delete Account) -->
  <div v-if="showDeleteAccountDialog" class="overlay">
    <div class="account-dialog delete-account-dialog">
      <h2 class="dialog-title">Download Your Messages</h2>
      <div class="delete-account-content">
        <!-- Before deleting, as if they want to save their messages -->
        <p class="delete-account-message">
          Before you delete your account, would you like to download your messages to a PDF?
        </p>
      </div>
      <!-- Action buttons for Delete Account dialog (Download Messages, No, Just Delete, Cancel)-->
      <div class="dialog-actions delete-account-actions">
        <button class="dialog-save-btn" type="button" @click="downloadMessagesAndDelete">
          Download Messages
        </button>
        <button class="dialog-cancel-btn no-download-btn" type="button" @click="proceedWithAccountDeletion">
          No, Just Delete My Account
        </button>
        <button class="dialog-cancel-btn" type="button" @click="showDeleteAccountDialog = false">
          Cancel
        </button>
      </div>
    </div>
  </div>

  <!-- Delete Confirmation Dialog (show if user has clicked Delete Account -> No, Just Delete) -->
  <div v-if="showDeleteConfirmationDialog" class="overlay">
    <div class="account-dialog delete-confirmation-dialog">
      <h2 class="dialog-title">Confirm Account Deletion</h2>
      <div class="delete-confirmation-content">
        <!-- Let them know deleting their account and messages is permanent -->
        <p class="delete-confirmation-message">
          Deleting your account is permanent. All of your information and messages will be deleted.
        </p>
        <p class="delete-confirmation-submessage">
          Are you sure you want to delete your account?
        </p>
      </div>
      <!-- Delete confirmation dialog action buttons (Cancel, Yes, Delete My Account)-->
      <div class="dialog-actions delete-confirmation-actions">
        <button class="dialog-cancel-btn" type="button" @click="showDeleteConfirmationDialog = false">
          Cancel
        </button>
        <button class="dialog-cancel-btn delete-yes-btn" type="button" @click="finalDeleteAccount">
          Yes, Delete My Account
        </button>
      </div>
    </div>
  </div>

  <!-- Account Deleted Message -->
  <div v-if="showAccountDeletedMessage" class="overlay">
    <div class="message">
      <div class="account-deleted-icon">✓</div>
      <h3 class="dialog-title">Account Deleted</h3>
      <p class="account-deleted-text">Your account has been permanently deleted. Thank you for using Hey, It's Me.</p>
    </div>
  </div>

  <!-- Cancel Subscription First Dialog -->
  <PopupDialog
    :show="showCancelSubscriptionFirstDialog"
    type="warning"
    title="Cancel Your Subscription First"
    message="Before you can delete your account, please cancel your SMS subscription. Click Manage Subscription from your Account Settings and then click Cancel Subscription. Then, you can delete your account."
    primary-button-text="OK"
    :show-cancel="false"
    @close="showCancelSubscriptionFirstDialog = false"
    @confirm="showCancelSubscriptionFirstDialog = false"
  />

  <!-- Session Expired Dialog -->
  <PopupDialog
    :show="showSessionExpiredDialog"
    type="warning"
    title="Session Ended"
    message="Your session ended, please log back in."
    primary-button-text="OK"
    :show-cancel="false"
    @close="onSessionExpiredOk"
    @confirm="onSessionExpiredOk"
  />

  <!-- SMS Opt-In (shown when user selects SMS delivery method) -->
  <div v-if="showSmsOptIn" class="overlay sms-optin-overlay">
    <SmsOptInPage :is-dialog="true" :is-included="isIncludedSms" @close="showSmsOptIn = false" @sms-enabled="onSmsEnabled" />
  </div>

  <!-- Switch to Email confirmation -->
  <div v-if="showDisableSmsConfirmation" class="overlay" style="z-index:2100;">
    <div class="account-dialog">
      <h2 class="dialog-title">Switch  back to email?</h2>
      <p style="color:#6b7280; font-size:0.97rem; line-height:1.5; margin: 0 0 20px;">Click Continue below to cancel your subscription. Once your subscription is canceled, you'll go back to receiving your messages via email and will no longer receive any SMS/text messages from Hey, It's Me. <br><br>IMPORTANT: Although your SMS functionality will end immediately, there are <strong>no partial refunds</strong> for any remaining days of your billing period.</p>
      <div class="dialog-actions">
        <button class="dialog-cancel-btn" type="button" @click="showDisableSmsConfirmation = false">Cancel</button>
        <button class="dialog-save-btn" type="button" @click="disableSms">
          <span v-if="!isDisablingSms">Continue</span>
          <span v-else class="spinner"></span>
        </button>
      </div>
    </div>
  </div>

</template>

<script>
import { jsPDF } from 'jspdf';
import { VerificationCodeValidation, EmailValidation } from '@/utils/validation';
import { useApi } from '@/composables/useApi';
import SmsOptInPage from '@/components/informational-pages/SmsOptInPage.vue';
import PopupDialog from '@/components/shared/PopupDialog.vue';

export default {
  name: 'AccountEditDialog',
  components: { SmsOptInPage, PopupDialog },
  props: {
    show: Boolean,
    account: Object,
    originalEmail: String,
    messages: Array // Needed for PDF download
  },
  emits: [
    'close',
    'account-updated',
    'account-deleted',
    'account-changed'
  ],
  data() {
    return {
      api: useApi(),
      localAccount: this.account ? { ...this.account, accountFree: Boolean(this.account.accountFree) } : {},
      emailChangeCodeRequestError: '',
      showDeleteAccountDialog: false,
      showDeleteConfirmationDialog: false,
      showVerificationDialog: false,
      showEmailVerifiedMessage: false,
      showAccountUpdatedMessage: false,
      showAccountDeletedMessage: false,
      showEmailVerificationRequiredMessage: false,
      showAccountUpdateAttemptsExceededDialog: false,
      isEmailVerified: true,
      emailChanged: false,
      verificationAttempts: 0,
      verificationError: '',
      verificationCode: '',
      isAccountSaving: false,
      isRequestingCode: false,
      isValidatingCode: false,
      selectedFrequency: this.account && this.account.msgFrequency ? this.mapApiFrequencyToUI(this.account.msgFrequency) : '1-2',
      frequencySaved: false,
      isFrequencySaving: false,
      showSmsOptIn: false,
      showDisableSmsConfirmation: false,
      isDisablingSms: false,
      isManagingSubscription: false,
      selectedDeliveryMethod: this.account?.selfMessageDeliveryMethod || 'Email',
      phoneNumberError: '',
      showSessionExpiredDialog: false,
      showCancelSubscriptionFirstDialog: false,
    };
  },
  computed: {
    // All-inclusive-plan accounts pay one $5/mo charge covering both channels regardless of
    // Trialing/Active/Restricted status, so there's no Stripe checkout/cancellation involved
    // when switching delivery methods. Only legacy accounts pay separately for SMS.
    isIncludedSms() {
      return this.account && this.account.pricingPlan === 'AllInclusiveSubscription';
    }
  },
  watch: {
    account: {
      handler(newVal) {
        this.localAccount = { ...newVal, accountFree: Boolean(newVal.accountFree) };
        this.emailChanged = false;
        this.isEmailVerified = true;
        // Map API frequency to UI dropdown value
        if (newVal && newVal.msgFrequency) {
          this.selectedFrequency = this.mapApiFrequencyToUI(newVal.msgFrequency);
        } else {
          this.selectedFrequency = '1-2';
        }
        this.selectedDeliveryMethod = newVal.selfMessageDeliveryMethod || 'Email';
        this.phoneNumberError = '';
      },
      deep: true
    },
  mounted() {
    // Set initial frequency from account prop on mount
    if (this.account && this.account.msgFrequency) {
      this.selectedFrequency = this.mapApiFrequencyToUI(this.account.msgFrequency);
    } else {
      this.selectedFrequency = '1-2';
    }
    // Ensure accountFree is always a boolean
    if (this.account) {
      this.localAccount = { ...this.account, accountFree: Boolean(this.account.accountFree) };
    }
  },
    'localAccount.email'(newEmail) {
      this.emailChanged = newEmail !== this.originalEmail;
      if (this.emailChanged) {
        this.isEmailVerified = false;
        this.verificationCode = '';
      } else {
        this.isEmailVerified = true;
      }
    },
    showVerificationDialog(newVal) {
      if (!newVal) {
        this.verificationError = '';
      }
    }
  },
  methods: {
    close() {
      this.$emit('close');
    },
    onSmsEnabled() {
      this.showSmsOptIn = false;
      this.$emit('account-changed');
      this.$emit('close');
    },
    async manageSubscription() {
      if (this.isManagingSubscription) return;
      this.isManagingSubscription = true;
      try {
        const result = await this.api.account.createCustomerPortalSession();
        this.isManagingSubscription = false;
        window.location.href = result.url;
      } catch (error) {
        alert('Unable to open subscription management. Please try again.');
        this.isManagingSubscription = false;
      }
    },
    async disableSms() {
      if (this.isDisablingSms) return;
      this.isDisablingSms = true;
      try {
        const result = await this.api.account.createCustomerPortalSession();
        this.isDisablingSms = false;
        window.location.href = result.url;
      } catch (error) {
        alert('Unable to open subscription management. Please try again.');
        this.isDisablingSms = false;
      }
    },
    async disableIncludedSms() {
      if (this.isDisablingSms) return;
      this.isDisablingSms = true;
      try {
        await this.api.account.disableIncludedSms();
        this.selectedDeliveryMethod = 'Email';
        this.$emit('account-changed');
      } catch (error) {
        if (error.isSessionExpired) {
          this.showSessionExpiredDialog = true;
        } else {
          alert('Unable to switch back to email. Please try again.');
        }
      } finally {
        this.isDisablingSms = false;
      }
    },
    onPhoneInput(e) {
      const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
      e.target.value = digits;
      this.localAccount.phoneNumber = digits;
      this.phoneNumberError = digits.length > 0 && digits.length < 10
        ? 'Please enter a 10-digit US phone number.'
        : '';
    },
    closeVerificationDialog() {
      this.showVerificationDialog = false;
    },
    async saveAccountInfo() {
      if (this.isAccountSaving) return;
      this.isAccountSaving = true;
      try {
        if (this.emailChanged && !this.isEmailVerified) {
          this.showEmailVerificationRequiredMessage = true;
          setTimeout(() => {
            this.showEmailVerificationRequiredMessage = false;
          }, 4000);
          return;
        }
        // Ensure msgFrequency is up to date from dropdown
        console.log('this.selectedFrequency:', this.selectedFrequency);
        this.localAccount.msgFrequency = this.mapUIFrequencyToAPI(this.selectedFrequency);
        console.log('this.localAccount.msgFrequency:', this.localAccount.msgFrequency);
        const verificationCode = this.emailChanged ? this.verificationCode : '';
        await this.api.account.updateAccount(
          this.localAccount.name,
          this.localAccount.email,
          verificationCode,
          this.localAccount.msgFrequency,
          (this.selectedDeliveryMethod === 'SMS' || this.selectedDeliveryMethod === 'SmsAwaitingConfirmation') ? (this.localAccount.phoneNumber || '') : '',
        );
        this.$emit('account-updated', { ...this.localAccount });
        // Do not mutate originalEmail prop. Let parent update it if needed.
        this.emailChanged = false;
        this.isEmailVerified = true;
        this.showAccountUpdatedMessage = true;
        setTimeout(() => {
          this.showAccountUpdatedMessage = false;
        }, 3000);
        this.close();
        this.verificationCode = '';
      } catch (error) {
        if (error.isSessionExpired) {
          this.showSessionExpiredDialog = true;
        } else if (error.message === 'phone_error') {
          alert("We weren't able to save this phone number. Please double-check it and try again.");
        } else {
          alert('Failed to update account. Please submit an Issue Report through the Contact Me link at the bottom of the page.');
        }
      } finally {
        this.isAccountSaving = false;
      }
    },

    // Map API frequency values to UI values
    mapApiFrequencyToUI(apiFrequency) {
      console.log('Mapping API frequency to UI:', apiFrequency);
      const mapping = {
        'Low': '1-2',
        'Medium': '5-7',
        'High': '10-12'
      };
      const result = mapping[apiFrequency] || '1-2';
      console.log('Mapping result:', result);
      return result;
    },
    // Map UI frequency values to API values
    mapUIFrequencyToAPI(uiFrequency) {
      const mapping = {
        '1-2': 'Low',
        '5-7': 'Medium',
        '10-12': 'High'
      };
      return mapping[uiFrequency] || 'Low';
    },
    async requestEmailVerification() {
      if (this.isRequestingCode) return;
      this.isRequestingCode = true;
      this.emailChangeCodeRequestError = '';
      try {
        const result = await this.api.account.requestAccountUpdateCode(this.localAccount.email);
        if (result.requiresVerification) {
          this.showVerificationDialog = true;
        } else {
          this.isEmailVerified = true;
        }
      } catch (error) {
        if (error.isSessionExpired) {
          this.showSessionExpiredDialog = true;
        } else {
          this.emailChangeCodeRequestError = 'Failed to send verification code. Please submit an Issue Report through the Contact Me link at the bottom of the page.';
        }
      } finally {
        this.isRequestingCode = false;
      }
    },
    async submitVerificationCode() {
      if (this.isValidatingCode) return;
      this.isValidatingCode = true;
      this.verificationError = '';
      try {
        const codeValidation = VerificationCodeValidation.process(this.verificationCode);
        const emailValidation = EmailValidation.process(this.localAccount.email);
        if (!codeValidation.isValid) {
          this.verificationError = codeValidation.error;
          return;
        }
        if (!emailValidation.isValid) {
          this.verificationError = emailValidation.error;
          return;
        }
        await this.api.account.verifyAccountUpdateCode(emailValidation.sanitized, codeValidation.sanitized);
        this.isEmailVerified = true;
        this.showVerificationDialog = false;
        this.verificationError = '';
        this.verificationAttempts = 0;
        this.showEmailVerifiedMessage = true;
        setTimeout(() => {
          this.showEmailVerifiedMessage = false;
        }, 3000);
      } catch (error) {
        if (error.isSessionExpired) {
          this.showSessionExpiredDialog = true;
        } else if (error.message.includes('Maximum verification attempts exceeded')) {
          this.showVerificationDialog = false;
          this.showAccountUpdateAttemptsExceededDialog = true;
        } else {
          this.verificationError = error.message;
        }
      } finally {
        this.isValidatingCode = false;
      }
    },
    onDeleteAccountClick() {
      if (this.selectedDeliveryMethod === 'SMS' || this.selectedDeliveryMethod === 'SmsAwaitingConfirmation') {
        this.showCancelSubscriptionFirstDialog = true;
      } else {
        this.showDeleteAccountDialog = true;
      }
    },
    onSessionExpiredOk() {
      this.showSessionExpiredDialog = false;
      this.$router.push({ path: '/', query: { login: 'required', redirect: '/account?edit=true' } });
    },
    restartEmailVerificationProcess() {
      this.showAccountUpdateAttemptsExceededDialog = false;
      this.isEmailVerified = false;
      this.verificationCode = '';
      this.verificationError = '';
      this.verificationAttempts = 0;
      this.requestEmailVerification();
    },
    downloadMessagesAndDelete() {
      // PDF generation logic
      try {
        const doc = new jsPDF();
        const primaryColor = [125, 164, 126];
        const textColor = [49, 45, 48];
        const lightGray = [136, 136, 136];
        const pageWidth = doc.internal.pageSize.width;
        const pageHeight = doc.internal.pageSize.height;
        const margin = 20;
        const contentWidth = pageWidth - (margin * 2);
        let currentY = margin;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(24);
        doc.setTextColor(...primaryColor);
        doc.text('Your Messages', pageWidth / 2, currentY, { align: 'center' });
        currentY += 20;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(14);
        doc.setTextColor(...textColor);
        if (!this.messages || this.messages.length === 0) {
          doc.setFont('helvetica', 'italic');
          doc.setFontSize(12);
          doc.setTextColor(...lightGray);
          doc.text('No messages have been created yet.', margin, currentY);
        } else {
          const sortedMessages = [...this.messages].sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate));
          sortedMessages.forEach((message) => {
            if (currentY > pageHeight - 60) {
              doc.addPage();
              currentY = margin;
            }
            const dateStr = message.createdDate ? 
              message.createdDate.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              }) : 'Date unknown';
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(10);
            doc.setTextColor(...lightGray);
            doc.text(dateStr, margin, currentY);
            currentY += 12; // less space after date
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(11);
            doc.setTextColor(...textColor);
            const messageLines = doc.splitTextToSize(message.text, contentWidth - 10);
            doc.text(messageLines, margin + 5, currentY);
            currentY += messageLines.length * 5 + 10; // less space after message
          });
        }
        const footerY = pageHeight - 15;
        doc.setFont('helvetica', 'italic');
        doc.setFontSize(8);
        doc.setTextColor(...lightGray);
        const exportDate = new Date().toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long', 
          day: 'numeric'
        });
        doc.text(`Generated on ${exportDate}`, pageWidth / 2, footerY, { align: 'center' });
        doc.save('Messages.pdf');
        this.showDeleteAccountDialog = false;
        this.showDeleteConfirmationDialog = true;
      } catch (error) {
        alert('Failed to generate PDF. Please try again.');
      }
    },
    proceedWithAccountDeletion() {
      this.showDeleteAccountDialog = false;
      this.showDeleteConfirmationDialog = true;
    },
    async finalDeleteAccount() {
      try {
        await this.api.account.deleteAccount();
        this.showAccountDeletedMessage = true;
        setTimeout(() => {
          this.showAccountDeletedMessage = false;
          this.$emit('account-deleted');
          this.close();
        }, 4000);
      } catch (error) {
        if (error.isSessionExpired) {
          this.showSessionExpiredDialog = true;
        } else {
          alert('Failed to delete account: ' + error.message);
        }
      } finally {
        this.showDeleteConfirmationDialog = false;
      }
    }
  }
};
</script>

<style scoped>
.message {
  background: white;
  border-radius: 12px;
  padding: 30px;
  text-align: center;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  max-width: 380px;
  width: 100%;
  animation: dialogSlideIn 0.3s ease-out;
}
.dialog-manage-sub-btn {
  background: #7da47e;
  color: #fff;
  border: none;
  padding: 8px 18px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: bold;
  transition: background 0.2s;
  margin-bottom: 6px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.03);
}
.dialog-manage-sub-btn:hover {
  background: #388E3C;
}
.error-message {
  color: #d32f2f;
  font-size: 0.97rem;
  margin-top: 4px;
}
input, select, textarea {
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 16px; /* Prevent mobile zoom */
  font-family: 'Quicksand', 'Segoe UI', 'Georgia', serif;
  width: 100%;
  box-sizing: border-box;
}
textarea {
  min-height: 80px;
  resize: vertical;
}
.dialog-form-group-row {
  flex-direction: row;
  align-items: center;
  gap: 8px;
}
.dialog-form-group label {
  font-weight: bold;
  color: #312d30;
  font-size: 1rem;
  text-align: left;
}
.dialog-form-group input {
  padding: 7px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 16px; /* Prevent mobile zoom */
  font-family: 'Quicksand', 'Segoe UI', 'Georgia', serif;
}
.dialog-validate-btn {
  background: #7da47e;
  color: #fff;
  border: none;
  padding: 7px 14px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: bold;
  transition: background 0.2s;
  margin-left: 4px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.03);
}
.dialog-validate-btn:hover {
  background: #388E3C;
}
.verification-warning {
  font-size: 0.9em;
  color: #d32f2f;
  margin-top: 8px;
  font-style: italic;
}

.verification-checkmark {
  color: #4caf50;
  font-size: 1.2em;
  font-weight: bold;
  padding: 7px 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 60px;
}

/* Verification info styling */
.verification-info {
  font-size: 0.9em;
  color: #666;
  margin-top: 8px;
  font-style: italic;
}

.code-sent-icon {
  width: 50px;
  height: 50px;
  background-color: #4caf50;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: bold;
  margin: 0 auto 16px;
}

.code-sent-text {
  margin: 0;
  font-size: 14px;
  color: #6b7280;
  font-family: 'Quicksand', sans-serif;
}


.email-verified-icon {
  width: 50px;
  height: 50px;
  background-color: #4caf50;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: bold;
  margin: 0 auto 16px;
}

.email-verified-text {
  margin: 0;
  font-size: 14px;
  color: #6b7280;
  font-family: 'Quicksand', sans-serif;
}

.account-updated-icon {
  width: 50px;
  height: 50px;
  background-color: #4caf50;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: bold;
  margin: 0 auto 16px;
}

.account-updated-text {
  margin: 0;
  font-size: 14px;
  color: #6b7280;
  font-family: 'Quicksand', sans-serif;
}

.dialog-phone-prefix {
  padding: 7px 10px;
  background: #f3f4f6;
  border: 1px solid #ccc;
  border-right: none;
  border-radius: 4px 0 0 4px;
  font-size: 1rem;
  color: #374151;
  white-space: nowrap;
}

.dialog-phone-row input {
  border-radius: 0 4px 4px 0;
}

.sms-optin-overlay {
  overflow-y: auto;
  align-items: flex-start;
}

.delivery-method-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.delivery-method-buttons {
  display: flex;
  gap: 10px;
}

.delivery-btn {
  flex: 1;
  padding: 10px 12px;
  border: 2px solid #ccc;
  border-radius: 8px;
  background: #fff;
  color: #312d30;
  font-size: 0.95rem;
  font-family: 'Quicksand', 'Segoe UI', 'Georgia', serif;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s, color 0.2s;
}

.delivery-btn:hover {
  border-color: #7da47e;
}

.delivery-btn-active {
  border-color: #7da47e;
  background: #f0f7f0;
  color: #276749;
  font-weight: bold;
}

.account-deleted-icon {
  width: 50px;
  height: 50px;
  background-color: #4caf50;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: bold;
  margin: 0 auto 16px;
}

.account-deleted-text {
  margin: 0;
  font-size: 14px;
  color: #6b7280;
  font-family: 'Quicksand', sans-serif;
  line-height: 1.4;
}

.email-verification-required-icon {
  width: 50px;
  height: 50px;
  background-color: #ff9800;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: bold;
  margin: 0 auto 16px;
}

.email-verification-required-text {
  margin: 0;
  font-size: 14px;
  color: #6b7280;
  font-family: 'Quicksand', sans-serif;
}

/* Loading state for buttons */
.loading {
  pointer-events: none;
  opacity: 0.8;
}

/* Attempts exceeded dialog specific styling */
.attempts-exceeded-dialog {
  max-width: 450px;
}

.attempts-exceeded-content {
  text-align: center;
  margin-bottom: 20px;
}

.attempts-exceeded-message {
  color: #dc2626;
  font-size: 1rem;
  margin-bottom: 12px;
  font-weight: 500;
}

.attempts-exceeded-suggestion {
  color: #6b7280;
  font-size: 0.95rem;
  margin: 0;
}

.attempts-exceeded-actions {
  justify-content: center;
}

.attempts-exceeded-actions .dialog-save-btn {
  background: #7da47e;
}

.attempts-exceeded-actions .dialog-save-btn:hover {
  background: #388E3C;
}
.button {
  height: 40px; /* Set a consistent height for both buttons */
  min-width: 100px; /* Optional: ensure buttons have a minimum width */
  padding: 0 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.dialog-form-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 10px;
}
.dialog-form-group, .dialog-form-group label, .dialog-form-group input, .dialog-form-group button
{
  font-family: 'Quicksand', 'Segoe UI', 'Georgia', serif;
}
.dialog-phone-row {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}
.dialog-phone-row input {
  flex: 1 1 0;
}
.dialog-validate-btn {
  background: #7da47e;
  color: #fff;
  border: none;
  padding: 7px 14px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: bold;
  transition: background 0.2s;
  margin-left: 4px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.03);
}
.dialog-validate-btn:hover {
  background: #388E3C;
}
.verification-warning {
  font-size: 0.9em;
  color: #d32f2f;
  margin-top: 8px;
  font-style: italic;
}
.verification-checkmark {
  color: #4caf50;
  font-size: 1.2em;
  font-weight: bold;
  padding: 7px 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 60px;
}
.dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 10px;
}
.dialog-save-btn {
  background: #7da47e;
  color: #fff;
  border: none;
  padding: 7px 18px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: bold;
  transition: background 0.2s;
}
.dialog-save-btn:hover {
  background: #388E3C;
}
.dialog-cancel-btn {
  background: #888;
  color: #fff;
  border: none;
  padding: 7px 18px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: bold;
  transition: background 0.2s;
}
.dialog-cancel-btn:hover {
  background: #555;
}
.delete-account-section {
  border-top: 1px solid #e5e5e5;
  padding-top: 20px;
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  text-align: center;
}
.sms-awaiting-notice {
  background: #fef9ec;
  border: 1px solid #f5c842;
  border-radius: 6px;
  padding: 10px 14px;
  font-size: 0.9rem;
  color: #7a6000;
  margin-bottom: 8px;
}
.manage-subscription-link {
  color: #7da47e;
  font-size: 1rem;
  text-decoration: none;
  cursor: pointer;
  font-weight: bold;
  display: flex;
  align-items: center;
  gap: 6px;
}
.manage-subscription-link:hover {
  color: #4a7a4c;
}
.delete-account-link {
  color: #d32f2f;
  font-size: 1rem;
  text-decoration: none;
  cursor: pointer;
  font-weight: bold;
}
.delete-account-link:hover {
  color: #b71c1c;
}
.delete-account-dialog {
  max-width: 450px;
}
.delete-account-content {
  text-align: center;
  margin-bottom: 20px;
}
.delete-account-message {
  color: #312d30;
  font-size: 1rem;
  margin-bottom: 12px;
  font-weight: 500;
  line-height: 1.5;
}
.delete-account-submessage {
  color: #6b7280;
  font-size: 0.95rem;
  margin: 0;
  line-height: 1.4;
}
.delete-account-actions {
  flex-direction: column;
  gap: 12px;
  align-items: stretch;
}
.delete-account-actions .dialog-save-btn {
  background: #7da47e;
  order: 1;
}
.delete-account-actions .dialog-save-btn:hover {
  background: #388E3C;
}
.delete-account-actions .no-download-btn {
  background: #dc2626;
  order: 2;
}
.delete-account-actions .no-download-btn:hover {
  background: #b91c1c;
}
.delete-account-actions .dialog-cancel-btn:not(.no-download-btn) {
  order: 3;
  background: #888;
}
.delete-account-actions .dialog-cancel-btn:not(.no-download-btn):hover {
  background: #555;
}
.delete-confirmation-dialog {
  max-width: 480px;
}
.delete-confirmation-content {
  text-align: center;
  margin-bottom: 20px;
}
.delete-confirmation-message {
  color: #dc2626;
  font-size: 1rem;
  margin-bottom: 12px;
  font-weight: 500;
  line-height: 1.5;
}
.delete-confirmation-submessage {
  color: #312d30;
  font-size: 1rem;
  margin: 0;
  line-height: 1.4;
  font-weight: 600;
}
.delete-confirmation-actions {
  flex-direction: column;
  gap: 12px;
  align-items: stretch;
}
.delete-confirmation-actions .delete-yes-btn {
  background: #dc2626;
  order: 1;
}
.delete-confirmation-actions .delete-yes-btn:hover {
  background: #b91c1c;
}
.delete-confirmation-actions .dialog-cancel-btn:not(.delete-yes-btn) {
  order: 2;
  background: #888;
}
.delete-confirmation-actions .dialog-cancel-btn:not(.delete-yes-btn):hover {
  background: #555;
}
.frequency-label {
  font-size: 1.0rem;
  color: #312d30;
  font-family: 'Quicksand', 'Segoe UI', 'Georgia', serif;
  font-weight: 500;
  flex: 1; /* Allow the label to expand and fill available space */
  text-align: center;
}
.frequency-controls {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: nowrap; /* Prevent wrapping on all screen sizes */
  flex-shrink: 0; /* Prevent the controls from shrinking */
}
.frequency-select {
  font-size: 0.97rem;
  padding: 5px 14px 5px 8px;
  border-radius: 4px;
  border: 1px solid #ccc;
  font-family: 'Quicksand', 'Segoe UI', 'Georgia', serif;
  background: #fff;
  color: #312d30;
  margin-left: 8px;
  height: 32px;
}
.frequency-save-btn {
  background: #7da47e;
  color: #fff;
  border: none;
  padding: 5px 10px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: bold;
  margin-left: 8px;
  transition: background 0.2s;
  box-shadow: 0 1px 2px rgba(0,0,0,0.03);
  display: flex;
  align-items: center;
  height: 32px;
  min-width: 60px;
  justify-content: center;
  white-space: nowrap;
}
.frequency-save-btn:disabled {
  background: #b5cbb2;
  cursor: not-allowed;
  opacity: 0.7;
}
.frequency-select {
  margin-left: 0;
  width: 180px; /* Slightly wider for better readability */
  flex-shrink: 0;
}
.frequency-save-btn {
  margin-left: 0;
  flex-shrink: 0; /* Prevent shrinking on all screen sizes */
}
.account-id-display {
  margin-bottom: 10px;
  text-align: center;
  font-size: 1rem;
  color: #888;
  font-family: 'Quicksand', 'Segoe UI', 'Georgia', serif;
}

@media (max-width: 800px) {
  .frequency-section {
    flex-direction: column;
    gap: 12px;
    text-align: left;
    align-items: flex-start;
    transform: scale(1);
    padding: 0 0px;
    justify-content: flex-start;
  }
  .frequency-label {
    font-size: 1.05rem;
    line-height: 1.4;
    flex: none;
    text-align: left !important;
    align-self: flex-start !important;
  }
  .frequency-controls {
    justify-content: flex-start !important;
    align-items: flex-start !important;
    width: 100%;
  }
  .frequency-select {
    width: auto;
    flex: 1;
    text-align: left !important;
    align-self: flex-start !important;
  }
}
</style>
